// ─── YogaGame_styles.js ───────────────────────────────────────────────────────
// Styled-components for the Yoga Game page.
// Uses the centralised COLORS / INDIGO_CSS_VARS token system from colors.js.
// ─────────────────────────────────────────────────────────────────────────────

import styled, { keyframes, css } from "styled-components";
import { COLORS, INDIGO_CSS_VARS } from "../../common/colors";

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const floatBob = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-8px); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 18px ${COLORS.primaryGlow}; }
  50%       { box-shadow: 0 0 36px ${COLORS.accentGlow}, 0 0 60px ${COLORS.primaryGlow}; }
`;

const spinSlow = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const scorePopIn = keyframes`
  0%   { transform: scale(0.6) translateY(-12px); opacity: 0; }
  70%  { transform: scale(1.15) translateY(0);    opacity: 1; }
  100% { transform: scale(1)    translateY(0);    opacity: 1; }
`;

const shakeCrash = keyframes`
  0%, 100% { transform: translateX(0); }
  20%       { transform: translateX(-6px); }
  40%       { transform: translateX(6px); }
  60%       { transform: translateX(-4px); }
  80%       { transform: translateX(4px); }
`;

const mandalaRotate = keyframes`
  from { transform: rotate(0deg); opacity: 0.06; }
  to   { transform: rotate(360deg); opacity: 0.06; }
`;

// ─── Page wrapper ─────────────────────────────────────────────────────────────

export const Section = styled.section`
  ${INDIGO_CSS_VARS}
  min-height: 100vh;
  background: ${COLORS.bgWarm};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 20px 60px;
  position: relative;
  overflow: hidden;

  /* Mandala watermark */
  &::before {
    content: "☸";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: clamp(320px, 50vw, 560px);
    color: ${COLORS.primary};
    opacity: 0.04;
    pointer-events: none;
    animation: ${mandalaRotate} 60s linear infinite;
    line-height: 1;
    user-select: none;
  }
`;

// ─── Header block ─────────────────────────────────────────────────────────────

export const GameHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
  animation: ${fadeInUp} 0.7s ease both;
`;

export const GameTitle = styled.h1`
  font-family: "Cinzel Decorative", "Palatino Linotype", serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 700;
  color: ${COLORS.primaryDark};
  letter-spacing: 0.04em;
  margin: 0 0 8px;
  line-height: 1.2;

  span {
    color: ${COLORS.primary};
  }
`;

export const GameSubtitle = styled.p`
  font-family: "EB Garamond", "Georgia", serif;
  font-size: 1.05rem;
  color: ${COLORS.textBody};
  margin: 0;
  letter-spacing: 0.02em;
`;

// ─── HUD row (score / hi-score / level) ──────────────────────────────────────

export const HudRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  justify-content: center;
  animation: ${fadeInUp} 0.7s 0.15s ease both;
`;

export const HudPill = styled.div`
  background: ${COLORS.surface92};
  border: 1.5px solid ${COLORS.borderGold20};
  border-radius: 40px;
  padding: 8px 22px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 12px ${COLORS.primaryShadow};

  .label {
    font-family: "EB Garamond", serif;
    font-size: 0.75rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${COLORS.textMid};
  }

  .value {
    font-family: "Cinzel Decorative", serif;
    font-size: 1.1rem;
    font-weight: 700;
    color: ${COLORS.primaryDark};
    min-width: 42px;
    text-align: right;

    &.pop {
      animation: ${scorePopIn} 0.35s ease;
    }
  }

  .icon {
    font-size: 1rem;
  }
`;

// ─── Canvas wrapper ───────────────────────────────────────────────────────────

export const CanvasWrapper = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 8px 40px ${COLORS.primaryShadow},
    0 0 0 3px ${COLORS.border},
    0 0 0 6px ${COLORS.borderGold20};
  animation: ${fadeInUp} 0.7s 0.25s ease both;

  ${({ $crashed }) =>
    $crashed &&
    css`
      animation: ${shakeCrash} 0.4s ease;
    `}

  canvas {
    display: block;
    border-radius: 18px;
  }
`;

// ─── Controls hint ────────────────────────────────────────────────────────────

export const ControlsHint = styled.div`
  margin-top: 18px;
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  animation: ${fadeInUp} 0.7s 0.35s ease both;
`;

export const KeyBadge = styled.span`
  background: ${COLORS.white};
  border: 2px solid ${COLORS.border};
  border-bottom-width: 4px;
  border-radius: 8px;
  padding: 5px 14px;
  font-family: "Cinzel Decorative", monospace;
  font-size: 0.78rem;
  color: ${COLORS.primaryDark};
  letter-spacing: 0.06em;
  box-shadow: 0 2px 8px ${COLORS.primaryShadow};
`;

export const HintText = styled.span`
  font-family: "EB Garamond", serif;
  font-size: 0.95rem;
  color: ${COLORS.textBody};
  align-self: center;
`;

// ─── Overlay (start / game-over / paused) ────────────────────────────────────

export const OverlayBox = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${COLORS.surface92};
  border-radius: 18px;
  gap: 18px;
  padding: 32px;
  text-align: center;
  backdrop-filter: blur(4px);
  z-index: 10;
`;

export const OverlayEmoji = styled.div`
  font-size: 3.5rem;
  animation: ${floatBob} 2.5s ease-in-out infinite;
`;

export const OverlayTitle = styled.h2`
  font-family: "Cinzel Decorative", serif;
  font-size: clamp(1.3rem, 3vw, 1.9rem);
  color: ${COLORS.primaryDark};
  margin: 0;
  line-height: 1.3;
`;

export const OverlayBody = styled.p`
  font-family: "EB Garamond", serif;
  font-size: 1rem;
  color: ${COLORS.textBody};
  margin: 0;
  max-width: 320px;
`;

export const StartButton = styled.button`
  font-family: "Cinzel Decorative", serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: ${COLORS.white};
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryLight} 100%);
  border: none;
  border-radius: 50px;
  padding: 14px 40px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  animation: ${glowPulse} 2.5s ease-in-out infinite;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 28px ${COLORS.primaryGlow};
  }

  &:active {
    transform: scale(0.97);
  }
`;

// ─── Level badge ──────────────────────────────────────────────────────────────

export const LevelBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${COLORS.accentTint};
  border: 1.5px solid ${COLORS.borderGold20};
  border-radius: 40px;
  padding: 5px 16px;
  font-family: "Cinzel Decorative", serif;
  font-size: 0.8rem;
  color: ${COLORS.accent};
  letter-spacing: 0.08em;
  margin-top: 8px;
`;

// ─── Lives row ────────────────────────────────────────────────────────────────

export const LivesRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 1.3rem;
  margin-bottom: 14px;
  animation: ${fadeInUp} 0.7s 0.1s ease both;
`;

// ─── Info strip below canvas ──────────────────────────────────────────────────

export const InfoStrip = styled.div`
  margin-top: 22px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  animation: ${fadeInUp} 0.7s 0.4s ease both;
`;

export const InfoChip = styled.div`
  background: ${COLORS.bgLight};
  border: 1px solid ${COLORS.borderGold20};
  border-radius: 12px;
  padding: 8px 18px;
  font-family: "EB Garamond", serif;
  font-size: 0.9rem;
  color: ${COLORS.textBody};
  display: flex;
  align-items: center;
  gap: 6px;

  strong {
    color: ${COLORS.primaryDark};
  }
`;
